/**
 * Consultant Module
 * 
 * Provides consultant management functionality for EvalPro.
 * Handles consultant information storage and management for both
 * registered users and third-party consultants in coach mode.
 * 
 * Implements requirements FRPR01 from the requirements document.
 */
import BaseModule, { NavigationItem, RouteItem, DashboardWidget, AnalyticsData, HelpContent } from './BaseModule';
import { People } from '@mui/icons-material';
import ConsultantList from './consultants/ConsultantList';
import ConsultantForm from './consultants/ConsultantForm';
import ConsultantDetails from './consultants/ConsultantDetails';
import React, { ReactNode } from 'react';

export class ConsultantModule extends BaseModule {
    constructor() {
        super();
        this.id = 'consultants';
        this.name = 'Consultant Management';
        this.description = 'Manage consultant information and profiles';
        this.version = '1.0.0';
        this.enabled = true;
    }

    /**
     * Get navigation items for the sidebar
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of navigation items
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        const items: NavigationItem[] = [];

        // Only admins can manage consultants
        if (this.hasAdminAccess(user, userProfile)) {
            items.push({
                name: 'Consultants',
                icon: <People />,
                path: '/consultants',
                module: this.id
            });
        }

        return items;
    }

    /**
     * Get React routes for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of route objects
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        return [
            {
                path: '/consultants',
                element: <ConsultantList />,
                module: this.id
            },
            {
                path: '/consultants/new',
                element: <ConsultantForm />,
                module: this.id
            },
            {
                path: '/consultants/:id',
                element: <ConsultantDetails />,
                module: this.id
            },
            {
                path: '/consultants/:id/edit',
                element: <ConsultantForm />,
                module: this.id
            }
        ];
    }

    /**
     * Get dashboard widgets for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of widget components
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        // Widgets would be defined here if any
        return [];
    }

    /**
     * Get analytics data for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Analytics data object
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        return {
            totalConsultants: 0,
            activeConsultants: 0,
            registeredConsultants: 0,
            thirdPartyConsultants: 0,
            consultantsByCountry: {},
            consultantsByCompany: {}
        };
    }

    /**
     * Get help content for the module
     * @returns {Object} Help content object
     */
    getHelpContent(user: any, userProfile: any): HelpContent {
        return {
            '/consultants': {
                title: 'Consultant Management',
                description: 'Manage consultant information including both registered users and third-party consultants in coach mode. Store basic attributes like name, company, email, phone, and country.',
                quickTips: [
                    'Add consultants for both registered users and third-party evaluations',
                    'Store complete contact information for better tracking',
                    'Use company field to group consultants by organization',
                    'Country information helps with regional analysis',
                    'Link consultants to presentations for comprehensive evaluation'
                ],
                faqs: [
                    {
                        question: 'What is the difference between registered and third-party consultants?',
                        answer: 'Registered consultants are active users of the system who can log in and manage their own presentations. Third-party consultants are external individuals being evaluated in coach mode.'
                    },
                    {
                        question: 'What information is required for consultants?',
                        answer: 'Basic required information includes full name, company, email, phone number, and country. Additional fields can be added as needed for your organization.'
                    },
                    {
                        question: 'Can consultants be linked to multiple presentations?',
                        answer: 'Yes, consultants can have multiple presentations evaluated over time, allowing for progress tracking and improvement analysis.'
                    }
                ]
            }
        };
    }

    /**
     * Get Firestore collections used by the module
     * @returns {Array} Array of collection names
     */
    getCollections(): string[] {
        return [
            'consultants',
            'consultantProfiles'
        ];
    }

    /**
     * Get Firestore security rules for the module
     * @returns {string} Security rules string
     */
    getSecurityRules(): string {
        return `
      // Consultant security rules
      match /consultants/{consultantId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          request.auth.token.role == 'globalAdmin'
        );
        allow create: if request.auth != null && (
          request.resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
        allow update: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
        allow delete: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
      }

      match /consultantProfiles/{profileId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          resource.data.userId == request.auth.uid ||
          request.auth.token.role == 'globalAdmin'
        );
        allow write: if request.auth != null && (
          request.auth.token.role == 'tenantAdmin' || 
          request.auth.token.role == 'globalAdmin' ||
          resource.data.userId == request.auth.uid
        );
      }
    `;
    }

    /**
     * Check if user has admin access
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {boolean} Whether user has admin access
     */
    hasAdminAccess(user: any, userProfile: any): boolean {
        if (!user || !userProfile) return false;
        const role = userProfile.role;
        return role === 'globalAdmin' || role === 'tenantAdmin';
    }

    /**
     * Check if the module is available for the current user
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {boolean} Whether the module is available
     */
    isAvailable(user: any, userProfile: any): boolean {
        return this.enabled && user && userProfile;
    }
}

export default ConsultantModule;
